/**
 * Phase 6 — Hire Logs tab (equipment_hire WO detail).
 * Daily hour-meter logs + monthly billable summary with min-guarantee.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Truck, Plus } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { formatINR } from '../../lib/format';

const inp = 'w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#3b4872]';
const LOG_BADGE = { draft: 'bg-slate-100 text-slate-600', submitted: 'bg-amber-50 text-amber-700', approved: 'bg-emerald-50 text-emerald-700' };
const SUM_BADGE = { draft: 'bg-slate-100 text-slate-600', approved: 'bg-blue-50 text-blue-700', invoiced: 'bg-emerald-50 text-emerald-700' };

export default function WOHireTab({ wo, onChanged }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [logs, setLogs] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [adding, setAdding] = useState(false);
  const [genForm, setGenForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([
        apiGet('/hire-logs', { work_order_id: wo.id, month }),
        apiGet('/hire-monthly-summaries', { work_order_id: wo.id }),
      ]);
      setLogs(l?.items || []);
      setSummaries(s?.items || []);
    } catch { setLogs([]); }
  }, [wo.id, month]);
  useEffect(() => { load(); }, [load]);

  const approveLog = async (log) => {
    setBusy(true);
    try { await apiPost(`/hire-logs/${log.id}/approve`); toast.success('Log approved'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      await apiPost('/hire-monthly-summaries/generate', {
        work_order_id: wo.id, month,
        fuel_rate_per_litre: Number(genForm.fuel_rate) || 0,
        operator_charges: Number(genForm.operator_charges) || 0,
        mob_demob_charges: Number(genForm.mob_demob) || 0,
        tds_pct: Number(genForm.tds_pct) || 2,
      });
      toast.success('Monthly summary generated');
      setGenForm(null); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const summaryAct = async (s, path) => {
    setBusy(true);
    try {
      await apiPost(`/hire-monthly-summaries/${s.id}/${path}`, {});
      toast.success(path === 'raise-invoice' ? 'Hire invoice registered in Vendor Invoices' : 'Done');
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (logs === null) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" /></div>;

  const totalBillable = logs.reduce((s, l) => s + (l.billable_hours || 0), 0);

  return (
    <div className="space-y-5" data-testid="wo-hire-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="kpi-overline flex items-center gap-1.5"><Truck size={11} /> Daily Hire Logs</div>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="text-xs border border-slate-200 rounded-sm px-2 py-1.5" data-testid="hire-month-input" />
        <span className="text-xs text-slate-500">{logs.length} logs · {totalBillable} billable hrs (min guarantee {wo.min_guarantee_hours} hrs)</span>
        {['approved', 'active'].includes(wo.status) && !adding && (
          <button onClick={() => setAdding(true)} className="ml-auto flex items-center gap-1 px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold" data-testid="hire-add-log-btn">
            <Plus size={11} /> Log Day
          </button>
        )}
      </div>

      {adding && <HireLogForm wo={wo} month={month} onDone={(saved) => { setAdding(false); if (saved) load(); }} />}

      <table className="w-full dense-table" data-testid="hire-logs-table">
        <thead><tr><th>Date</th><th>Shift</th><th className="text-right">Start HM</th><th className="text-right">End HM</th><th className="text-right">Gross</th><th className="text-right">Breakdown</th><th className="text-right">Billable</th><th className="text-right">Fuel (L)</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {logs.length === 0 && <tr><td colSpan="10" className="text-center py-8 text-slate-400 text-xs">No logs for {month}</td></tr>}
          {logs.map((l) => (
            <tr key={l.id} data-testid={`hire-log-${l.log_date}`}>
              <td className="text-xs font-mono">{l.log_date}</td>
              <td className="text-xs">{l.shift}</td>
              <td className="text-right tabular-nums text-xs">{l.start_hour_meter}</td>
              <td className="text-right tabular-nums text-xs">{l.end_hour_meter}</td>
              <td className="text-right tabular-nums text-xs">{l.gross_hours}</td>
              <td className="text-right tabular-nums text-xs text-red-600">{l.breakdown_hours || 0}</td>
              <td className="text-right tabular-nums text-xs font-display font-bold">{l.billable_hours}</td>
              <td className="text-right tabular-nums text-xs">{l.fuel_issued_litres || 0}</td>
              <td><span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-display font-bold uppercase ${LOG_BADGE[l.status] || ''}`}>{l.status}</span></td>
              <td>
                {l.status !== 'approved' && (
                  <button onClick={() => approveLog(l)} disabled={busy} className="px-2 py-1 text-[10px] bg-emerald-600 text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid={`hire-log-approve-${l.log_date}`}>Approve</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Monthly summaries */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="kpi-overline">Monthly Summaries</div>
          {!genForm && (
            <button onClick={() => setGenForm({ tds_pct: 2 })} className="px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold" data-testid="hire-generate-btn">
              Generate {month} Summary
            </button>
          )}
        </div>
        {genForm && (
          <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3" data-testid="hire-gen-form">
            <label className="text-[10px] text-slate-500">Fuel rate ₹/L (client fuel recovery)<input type="number" value={genForm.fuel_rate || ''} onChange={(e) => setGenForm({ ...genForm, fuel_rate: e.target.value })} className={`${inp} mt-0.5`} data-testid="hire-gen-fuel-rate" /></label>
            <label className="text-[10px] text-slate-500">Operator charges ₹<input type="number" value={genForm.operator_charges || ''} onChange={(e) => setGenForm({ ...genForm, operator_charges: e.target.value })} className={`${inp} mt-0.5`} /></label>
            <label className="text-[10px] text-slate-500">Mob/demob ₹<input type="number" value={genForm.mob_demob || ''} onChange={(e) => setGenForm({ ...genForm, mob_demob: e.target.value })} className={`${inp} mt-0.5`} /></label>
            <label className="text-[10px] text-slate-500">TDS %<input type="number" value={genForm.tds_pct} onChange={(e) => setGenForm({ ...genForm, tds_pct: e.target.value })} className={`${inp} mt-0.5`} /></label>
            <div className="flex items-end gap-2">
              <button onClick={generate} disabled={busy} className="px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid="hire-gen-save">Generate</button>
              <button onClick={() => setGenForm(null)} className="px-2 py-1.5 text-xs text-slate-600">Cancel</button>
            </div>
          </div>
        )}
        <div className="space-y-2">
          {summaries.length === 0 && <div className="text-xs text-slate-400 py-4 text-center">No summaries generated</div>}
          {summaries.map((s) => (
            <div key={s.id} className="border border-slate-200 rounded-sm p-3" data-testid={`hire-summary-${s.month}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs">{s.summary_no}</span>
                <span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-display font-bold uppercase ${SUM_BADGE[s.status] || ''}`}>{s.status}</span>
                {s.min_guarantee_applied && <span className="px-1.5 py-0.5 text-[10px] bg-amber-50 text-amber-700 rounded-sm font-display">Min guarantee applied</span>}
                <div className="ml-auto flex gap-1.5">
                  {s.status === 'draft' && <button onClick={() => summaryAct(s, 'approve')} disabled={busy} className="px-2 py-1 text-[10px] bg-emerald-600 text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid={`hire-summary-approve-${s.month}`}>Approve</button>}
                  {s.status === 'approved' && <button onClick={() => summaryAct(s, 'raise-invoice')} disabled={busy} className="px-2 py-1 text-[10px] bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid={`hire-summary-invoice-${s.month}`}>Raise Invoice</button>}
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-2 text-xs">
                <Mini label="Billable hrs" value={s.total_billable_hours} />
                <Mini label="Billed hrs" value={s.billed_hours} />
                <Mini label="Hire amount" value={formatINR(s.gross_amount)} />
                <Mini label="Fuel recovery" value={`− ${formatINR(s.fuel_charges)}`} />
                <Mini label="Invoice value" value={formatINR(s.gross_invoice_value)} />
                <Mini label="Net (after TDS)" value={formatINR(s.net_payable)} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Mini = ({ label, value }) => (
  <div><div className="kpi-overline">{label}</div><div className="font-display font-bold mt-0.5">{value}</div></div>
);

function HireLogForm({ wo, month, onDone }) {
  const [f, setF] = useState({ log_date: `${month}-01`, shift: 'day', start: '', end: '', breakdown: '0', reason: '', fuel: '0', operator: '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await apiPost('/hire-logs', {
        work_order_id: wo.id, log_date: f.log_date, shift: f.shift,
        start_hour_meter: Number(f.start), end_hour_meter: Number(f.end),
        breakdown_hours: Number(f.breakdown) || 0, breakdown_reason: f.reason,
        fuel_issued_litres: Number(f.fuel) || 0, operator_name: f.operator,
      });
      toast.success('Log recorded');
      onDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="hire-log-form">
      <label className="text-[10px] text-slate-500">Date<input type="date" value={f.log_date} onChange={(e) => setF({ ...f, log_date: e.target.value })} className={`${inp} mt-0.5`} data-testid="hire-form-date" /></label>
      <label className="text-[10px] text-slate-500">Shift<select value={f.shift} onChange={(e) => setF({ ...f, shift: e.target.value })} className={`${inp} mt-0.5`}><option value="day">Day</option><option value="night">Night</option></select></label>
      <label className="text-[10px] text-slate-500">Start hour meter<input type="number" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className={`${inp} mt-0.5`} data-testid="hire-form-start" /></label>
      <label className="text-[10px] text-slate-500">End hour meter<input type="number" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className={`${inp} mt-0.5`} data-testid="hire-form-end" /></label>
      <label className="text-[10px] text-slate-500">Breakdown hrs<input type="number" value={f.breakdown} onChange={(e) => setF({ ...f, breakdown: e.target.value })} className={`${inp} mt-0.5`} /></label>
      <label className="text-[10px] text-slate-500">Breakdown reason<input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className={`${inp} mt-0.5`} /></label>
      <label className="text-[10px] text-slate-500">Fuel issued (L)<input type="number" value={f.fuel} onChange={(e) => setF({ ...f, fuel: e.target.value })} className={`${inp} mt-0.5`} /></label>
      <label className="text-[10px] text-slate-500">Operator<input value={f.operator} onChange={(e) => setF({ ...f, operator: e.target.value })} className={`${inp} mt-0.5`} /></label>
      <div className="col-span-full flex gap-2">
        <button onClick={save} disabled={saving} className="px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid="hire-form-save">{saving ? 'Saving…' : 'Save Log'}</button>
        <button onClick={() => onDone(false)} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-sm">Cancel</button>
      </div>
    </div>
  );
}
